import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Checkbox, Col, Row, Select, Spin, Alert, Result } from 'antd'
import { RobotOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useHistory, useLocation } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { authentication } from '../../Firebase'
import {
  approveMcpConsent,
  denyMcpConsent,
  getMcpConsentRequest,
} from 'api/McpOAuth/McpOAuth'
import {
  McpConsentRequest,
  McpSubjectType,
} from 'types/mcpOAuth/mcpOAuth'

const ORANGE = '#E69741'
const BORDER = '#EBEDF1'
const MUTED = '#6B6B70'
const TINT = '#FEF9F3'

const McpConsent = () => {
  const location = useLocation()
  const history = useHistory()
  const requestId = new URLSearchParams(location.search).get('request_id') ?? ''

  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>('')
  const [request, setRequest] = useState<McpConsentRequest | null>(null)
  const [scopes, setScopes] = useState<string[]>([])
  const [subject, setSubject] = useState<McpSubjectType | undefined>()
  const [approved, setApproved] = useState(false)

  // ── LOGIN GATE ───────────────────────────────────────────────────────────────────────────────
  //
  // This route is public so the `request_id` survives arrival, but the decision itself needs a
  // logged-in person — that is how the API learns whose data the token will read. A logged-out
  // visitor gets stashed and sent to sign in; the auth saga brings them straight back here.
  useEffect(() => {
    const stop = onAuthStateChanged(authentication, user => {
      if (user) {
        setSignedIn(true)
        return
      }
      setSignedIn(false)
      try {
        sessionStorage.setItem('postLoginRedirect', location.pathname + location.search)
      } catch (error) {
        // Storage disabled — they will land on the dashboard and can retry from the assistant.
      }
      history.push('/')
    })
    return () => stop()
  }, [history, location.pathname, location.search])

  const load = useCallback(async () => {
    if (!requestId) {
      setError(
        'This link is incomplete. Start the connection again from your AI assistant.',
      )
      setLoading(false)
      return
    }
    try {
      const data = await getMcpConsentRequest(requestId)
      setRequest(data)
      // Everything ticked by default, so the common case is one click — but each one can be
      // removed, and unticking is what makes this screen a decision rather than a notice.
      setScopes(data.requestedScopes ?? [])
      setSubject(data.availableSubjects?.[0])
    } catch (err: any) {
      setError(
        err?.message ??
          'That connection request is no longer valid. Start again from your AI assistant.',
      )
    } finally {
      setLoading(false)
    }
  }, [requestId])

  useEffect(() => {
    // Only once Firebase has confirmed a session — the API call needs its ID token.
    if (signedIn) load()
  }, [signedIn, load])

  const onApprove = async () => {
    if (!subject || scopes.length === 0) return
    setSubmitting(true)
    setError('')
    try {
      const result = await approveMcpConsent({ requestId, scopes, subjectType: subject })
      setApproved(true)
      // A full navigation, not a router push: the destination is the AI client's own domain and it
      // carries the authorization code.
      window.location.href = result.redirectTo
    } catch (err: any) {
      setError(err?.message ?? 'Could not complete the connection. Please try again.')
      setSubmitting(false)
    }
  }

  const onDeny = async () => {
    setSubmitting(true)
    setError('')
    try {
      const result = await denyMcpConsent(requestId)
      // Tell the waiting client explicitly, so it stops cleanly instead of timing out.
      window.location.href = result.redirectTo
    } catch (err: any) {
      setError(err?.message ?? 'Could not cancel the connection.')
      setSubmitting(false)
    }
  }
  
  const moduleLabels = request?.scopeModuleLabels ?? {}
  const actionLabels = request?.scopeActionLabels ?? {}
  // Reads first, then anything that changes something. Mirrors SCOPE_ACTION_ORDER in the backend's
  // `core/mcp/consent-copy.ts`; an action missing here sorts last rather than jumping to the front,
  // so the two drifting apart degrades the ordering instead of breaking the screen.
  const ACTION_ORDER = ['read', 'create', 'update', 'delete', 'write', 'switch']

  const titleCase = (value: string) =>
    value.replace(/[-_]+/g, ' ').replace(/^\w/, c => c.toUpperCase())

  // Scopes are grouped by module, and every ACTION in a module gets its own line inside that block.
  //
  // It used to collapse to one line per module — one checkbox, one description, actions joined as a
  // chip ("Organizations (Read · Switch)"). That hid two things once a non-read scope existed: the
  // switch description never rendered (the group kept the FIRST non-empty one, which is the read's),
  // and a single checkbox meant declining the write also declined the directory. One line per action
  // fixes both, and the module block still reads as one subject.
  const groups = React.useMemo(() => {
    const byModule = new Map<
      string,
      {
        module: string
        label: string
        scopes: string[]
        items: { scope: string; actionLabel: string; description: string }[]
      }
    >()

    for (const scope of request?.requestedScopes ?? []) {
      const [moduleKey, actionKey = 'read'] = scope.split(':')
      const item = {
        scope,
        actionLabel: actionLabels[actionKey] ?? titleCase(actionKey),
        description: request?.scopeDescriptions?.[scope] ?? '',
      }

      const existing = byModule.get(moduleKey)
      if (existing) {
        existing.scopes.push(scope)
        existing.items.push(item)
        continue
      }

      byModule.set(moduleKey, {
        module: moduleKey,
        label: moduleLabels[moduleKey] ?? titleCase(moduleKey),
        scopes: [scope],
        items: [item],
      })
    }

    // `Array.from`, not a spread — this tsconfig targets below es2015 and cannot iterate a Map.
    return Array.from(byModule.values()).map(group => ({
      ...group,
      // Reads first, then anything that changes something, so a write is never buried above a read.
      items: group.items.slice().sort((a, b) => {
        const rank = (label: string) => {
          const i = ACTION_ORDER.indexOf(label.toLowerCase())
          // Anything unrecognised sorts last rather than jumping to the front.
          return i === -1 ? ACTION_ORDER.length : i
        }
        return rank(a.actionLabel) - rank(b.actionLabel)
      }),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request])

  // A row is on only when every scope behind it is granted — partial state cannot be expressed by a
  // single box, and a half-granted module must never look fully granted.
  const groupChecked = (group: { scopes: string[] }) => group.scopes.every(s => scopes.includes(s))

  // True when SOME but not all of a module's scopes are on — the header box shows this as
  // indeterminate rather than pretending the module is fully granted or fully declined.
  const groupPartly = (group: { scopes: string[] }) =>
    group.scopes.some(s => scopes.includes(s)) && !group.scopes.every(s => scopes.includes(s))

  const toggleScope = (scope: string, on: boolean) => {
    setScopes(current => {
      const next = new Set(current)
      if (on) next.add(scope)
      else next.delete(scope)
      // Preserve the order the connector asked in, so the payload is stable and reviewable.
      return (request?.requestedScopes ?? []).filter(s => next.has(s))
    })
  }

  const toggleGroup = (group: { scopes: string[] }, on: boolean) => {
    setScopes(current => {
      const next = new Set(current)
      group.scopes.forEach(scope => (on ? next.add(scope) : next.delete(scope)))
      // Preserve the order the connector asked in, so the payload is stable and reviewable.
      return (request?.requestedScopes ?? []).filter(s => next.has(s))
    })
  }

  const allOn = groups.length > 0 && groups.every(groupChecked)
  const toggleAll = () => setScopes(allOn ? [] : [...(request?.requestedScopes ?? [])])

  const subjectLabel: Record<string, string> = {
    organizer: 'Organizer — your organization’s data',
    admin: 'Admin — every organization',
    guardian: 'Parent — your own family',
    family: 'Family — your own family',
  }

  if (signedIn === null || (signedIn && loading)) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Row>
    )
  }

  if (approved) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Result
          status="success"
          title="Connected"
          subTitle="Taking you back to your assistant…"
        />
      </Row>
    )
  }

  if (signedIn === false) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Spin size="large" tip="Taking you to sign in…" />
      </Row>
    )
  }

  if (error && !request) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Col xs={22} sm={18} md={12} lg={9}>
          <Result status="warning" title="Connection request unavailable" subTitle={error} />
        </Col>
      </Row>
    )
  }

  return (
    <Row justify="center" style={{ minHeight: '100vh', padding: '48px 16px', background: '#FAFAFA' }}>
      <Col xs={24} sm={22} md={16} lg={11} xl={9}>
        <Card bordered style={{ borderRadius: 12, borderColor: BORDER }}>
          <h2 className="f-semibold" style={{ marginBottom: 4 }}>
            Connect an AI assistant?
          </h2>
          <p style={{ color: '#6B6B70', marginBottom: 24 }}>
            Choose what it may read. You can approve less than it asked for.
          </p>

          {/* The connector, as it describes ITSELF. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 16px',
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
              marginBottom: 10,
            }}
          >
            <RobotOutlined style={{ fontSize: 22, color: ORANGE }} />
            <div>
              <div className="f-semibold" style={{ fontSize: 16 }}>
                {request?.client?.name || 'An AI assistant'}
              </div>
              {request?.client?.uri && (
                <div style={{ color: '#6B6B70', fontSize: 13 }}>{request.client.uri}</div>
              )}
            </div>
          </div>

          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 24, borderRadius: 8 }}
            message="This name has not been verified"
            description="It was supplied by the connector itself. Only continue if you just started this from your own AI assistant."
          />

          <div className="f-semibold" style={{ fontSize: 13, color: '#6B6B70', marginBottom: 8 }}>
            CONNECT AS
          </div>
          <Select
            value={subject}
            onChange={(value: McpSubjectType) => setSubject(value)}
            style={{ width: '100%', marginBottom: 24 }}
            options={(request?.availableSubjects ?? []).map(s => ({
              value: s,
              label: subjectLabel[s] ?? s,
            }))}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 10,
            }}
          >
            <span className="f-semibold" style={{ fontSize: 13, color: MUTED, letterSpacing: '.04em' }}>
              IT WILL BE ABLE TO
            </span>
            {groups.length > 1 && (
              <Button
                type="link"
                size="small"
                onClick={toggleAll}
                style={{ padding: 0, height: 'auto', color: ORANGE, fontSize: 13 }}
              >
                {allOn ? 'Clear all' : 'Select all'}
              </Button>
            )}
          </div>

          {groups.map(group => {
            const on = groupChecked(group)
            const partly = groupPartly(group)
            const anyOn = on || partly

            // A module with ONE action stays a single row with a single box — 11 of 12 modules are
            // read-only, and a header checkbox above an identical item checkbox is just clutter. The
            // split layout appears only where there is genuinely more than one decision to make.
            if (group.items.length === 1) {
              const only = group.items[0]
              return (
                <label
                  key={group.module}
                  style={{
                    display: 'block',
                    border: `1px solid ${on ? ORANGE : BORDER}`,
                    background: on ? TINT : '#FFFFFF',
                    borderRadius: 10,
                    padding: '13px 15px',
                    marginBottom: 8,
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'border-color .15s, background .15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                    <Checkbox
                      checked={on}
                      onChange={e => toggleScope(only.scope, e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                        }}
                      >
                        <span className="f-semibold" style={{ fontSize: 15 }}>
                          {group.label}
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 11,
                            letterSpacing: '.06em',
                            textTransform: 'uppercase',
                            color: on ? '#8A5A18' : MUTED,
                            background: on ? '#F7E6CF' : '#F3F4F6',
                            border: `1px solid ${on ? '#EBD3B0' : BORDER}`,
                            borderRadius: 999,
                            padding: '2px 9px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {only.actionLabel}
                        </span>
                      </div>
                      {only.description && (
                        <div style={{ color: MUTED, fontSize: 13, lineHeight: 1.45, marginTop: 3 }}>
                          {only.description}
                        </div>
                      )}
                    </div>
                  </div>
                </label>
              )
            }

            return (
              // Two or more actions. A DIV, not a label: each action inside is its own <label>, and
              // labels cannot nest.
              <div
                key={group.module}
                style={{
                  border: `1px solid ${anyOn ? ORANGE : BORDER}`,
                  background: anyOn ? TINT : '#FFFFFF',
                  borderRadius: 10,
                  marginBottom: 8,
                  width: '100%',
                  overflow: 'hidden',
                  transition: 'border-color .15s, background .15s',
                }}
              >
                {/* Module header — one subject, and a box that grants or declines the whole of it. */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '12px 15px',
                    cursor: 'pointer',
                    // Only when there is more than one action to separate from.
                    borderBottom: group.items.length > 1 ? `1px solid ${anyOn ? '#EBD3B0' : BORDER}` : 'none',
                  }}
                >
                  <Checkbox
                    checked={on}
                    indeterminate={partly}
                    onChange={e => toggleGroup(group, e.target.checked)}
                  />
                  <span className="f-semibold" style={{ fontSize: 15 }}>
                    {group.label}
                  </span>
                </label>

                {/* One line per action. Each carries its OWN description and its OWN checkbox, so a
                    write can be declined without also declining the read beside it. */}
                {group.items.map(item => {
                  const itemOn = scopes.includes(item.scope)
                  return (
                    <label
                      key={item.scope}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 11,
                        padding: '11px 15px 11px 41px',
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox
                        checked={itemOn}
                        onChange={e => toggleScope(item.scope, e.target.checked)}
                        style={{ marginTop: 2 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            fontSize: 11,
                            letterSpacing: '.06em',
                            textTransform: 'uppercase',
                            color: itemOn ? '#8A5A18' : MUTED,
                            background: itemOn ? '#F7E6CF' : '#F3F4F6',
                            border: `1px solid ${itemOn ? '#EBD3B0' : BORDER}`,
                            borderRadius: 999,
                            padding: '2px 9px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.actionLabel}
                        </span>
                        {item.description && (
                          <div style={{ color: MUTED, fontSize: 13, lineHeight: 1.45, marginTop: 4 }}>
                            {item.description}
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )
          })}

          {scopes.length === 0 && groups.length > 0 && (
            <div style={{ color: MUTED, fontSize: 13, marginTop: 10 }}>
              Nothing selected — approve at least one, or cancel the connection.
            </div>
          )}

          {error && (
            <Alert type="error" showIcon style={{ margin: '16px 0', borderRadius: 8 }} message={error} />
          )}

          <Row gutter={12} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Button block size="large" onClick={onDeny} disabled={submitting}>
                Cancel
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block
                size="large"
                type="primary"
                onClick={onApprove}
                loading={submitting}
                disabled={scopes.length === 0 || !subject}
                style={{ background: ORANGE, borderColor: ORANGE }}
              >
                Approve
              </Button>
            </Col>
          </Row>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: '#6B6B70',
              fontSize: 12,
              marginTop: 18,
            }}
          >
            <SafetyCertificateOutlined />
            You can disconnect this assistant at any time from your Bamrec account.
          </div>
        </Card>
      </Col>
    </Row>
  )
}

export default McpConsent
